// Copyright ©️ 2026 eVolve MEP, LLC

import { useEvolvePaginatedGet, type Facility } from '@evolvemep/api-ts';
import { Flex, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WorkOrderActionModalProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/workOrderActions';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

const SmallDetail = ({ title, value }: { title: string; value: string }) => (
  <Flex direction="column" style={{ width: '100%' }}>
    <Text fz="sm" c="dimmed">
      {title}
    </Text>
    <Text>{value}</Text>
  </Flex>
);

const TransferWorkOrderForm = ({
  workOrder,
  onWorkOrderActioned,
}: Pick<WorkOrderActionModalProps, 'workOrder' | 'onWorkOrderActioned'>) => {
  const { transferWorkOrder, mutationRunning: loading } = useWorkOrders();

  const paginatedFacilitiesGet = useEvolvePaginatedGet('shop/facility');

  const form = useForm<{
    facility: Pick<Facility, 'facilityId' | 'facilityName'>;
    reason: string;
  }>({
    validate: {
      facility: (v) => !v,
      reason: (v) => !v,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    await transferWorkOrder(workOrder.workOrderId, values.reason, values.facility.facilityId).then(() => {
      notifications.show({
        title: 'Successfully transferred',
        message: `${workOrder.name} transferred to facility ${values.facility.facilityName}.`,
        color: 'green',
      });
      onWorkOrderActioned();
    });
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <Flex gap="lg">
        <SmallDetail title="Project" value={workOrder.projectName} />
        <SmallDetail title="Current Facility" value={workOrder.facilityName} />
      </Flex>
      <SearchableSelect
        label="New Facility"
        idKey="facilityId"
        autoFocus
        data-autofocus
        paginatedGet={paginatedFacilitiesGet}
        nothingFoundMessage="No facilities available for transfer."
        getItemLabel={(f: Facility) => f.facilityName}
        disabled={loading}
        required
        preloadData
        placeholder="Please select"
        excludeOption={(o: Facility) => o.facilityId === workOrder.facilityId}
        autoSelectWhenOnlyOneOption
        {...form.getInputProps('facility')}
      />
      <Textarea
        required
        disabled={loading}
        label="Reason"
        placeholder="Required"
        maxLength={1000}
        {...form.getInputProps('reason')}
      />
    </>
  );
};

export const TransferWorkOrderModal = ({ workOrder, onWorkOrderActioned, ...props }: WorkOrderActionModalProps) => (
  <FormModal title={`Transfer ${workOrder.name}`} type="submit" confirmationText="Transfer" {...props}>
    <TransferWorkOrderForm workOrder={workOrder} onWorkOrderActioned={onWorkOrderActioned} />
  </FormModal>
);
